import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { wallBuilderInputsActions } from './inputsSlice';
import { calculateDerivedState } from './reducer';
import { generateSegmentId } from '../utils';
// Redux hooks for wall builder inputs
export const useWallBuilderInputsState = () => {
    return useSelector((state) => state.wallBuilderInputs || {
        wallType: null,
        zoom: 10,
        segments: [],
        selectedBlock: null,
        viewMode: 'front',
        selectedTopStoneId: '',
        selectedEndStoneId: '',
        selectedTopCornerStoneId: '',
        isAddingToCart: false,
    });
};
// Hook to compute calculations from inputs using useMemo
export const useWallBuilderCalculations = () => {
    const inputsState = useWallBuilderInputsState();
    return useMemo(() => {
        return calculateDerivedState(inputsState);
    }, [inputsState]);
};
// Combined state hook for backward compatibility
export const useWallBuilderState = () => {
    const inputs = useWallBuilderInputsState();
    const calculations = useWallBuilderCalculations();
    return useMemo(() => (Object.assign(Object.assign({}, inputs), calculations)), [inputs, calculations]);
};
export const useWallBuilderDispatch = () => {
    const dispatch = useDispatch();
    const inputsState = useWallBuilderInputsState();
    return useMemo(() => ({
        // Core state
        setWallType: (wallType) => {
            dispatch(wallBuilderInputsActions.setWallType(wallType));
        },
        setZoom: (zoom) => {
            dispatch(wallBuilderInputsActions.setZoom(zoom));
        },
        setSelectedBlock: (block) => {
            dispatch(wallBuilderInputsActions.setSelectedBlock(block));
        },
        setViewMode: (viewMode) => {
            dispatch(wallBuilderInputsActions.setViewMode(viewMode));
        },
        setGlobalStoneIds: (payload) => {
            dispatch(wallBuilderInputsActions.setGlobalStoneIds(payload));
        },
        setIsAddingToCart: (isAddingToCart) => {
            dispatch(wallBuilderInputsActions.setIsAddingToCart(isAddingToCart));
        },
        // Segment management
        addSegment: () => {
            var _a, _b;
            const newSegment = {
                id: generateSegmentId(),
                name: `Segment ${inputsState.segments.length + 1}`,
                height: '',
                length: '',
                cornerStones: 0,
                topStone: Boolean(((_a = inputsState.selectedBlock) === null || _a === void 0 ? void 0 : _a.top_stones) && inputsState.selectedBlock.top_stones.length > 0),
                startingStone: false,
                endStone: false,
                topCornerStone: Boolean((_b = inputsState.selectedBlock) === null || _b === void 0 ? void 0 : _b.corner_top),
            };
            dispatch(wallBuilderInputsActions.addSegment(newSegment));
        },
        removeSegment: (segmentId) => {
            dispatch(wallBuilderInputsActions.removeSegment(segmentId));
        },
        updateSegment: (segmentId, field, value) => {
            dispatch(wallBuilderInputsActions.updateSegment({ segmentId, field, value }));
        },
        updateSegmentName: (segmentId, name) => {
            dispatch(wallBuilderInputsActions.updateSegmentName({ segmentId, name }));
        },
        updateSegmentCornerStones: (segmentId, hasCornerStones) => {
            dispatch(wallBuilderInputsActions.updateSegmentCornerStones({ segmentId, hasCornerStones }));
        },
        updateSegmentTopStone: (segmentId, topStone) => {
            dispatch(wallBuilderInputsActions.updateSegmentTopStone({ segmentId, topStone }));
        },
        updateSegmentStartingStone: (segmentId, startingStone) => {
            dispatch(wallBuilderInputsActions.updateSegmentStartingStone({ segmentId, startingStone }));
        },
        updateSegmentEndStone: (segmentId, endStone) => {
            dispatch(wallBuilderInputsActions.updateSegmentEndStone({ segmentId, endStone }));
        },
        reorderSegments: (startIndex, endIndex) => {
            dispatch(wallBuilderInputsActions.reorderSegments({ startIndex, endIndex }));
        },
        // Zoom controls
        zoomIn: () => {
            const newZoom = Math.min(20, inputsState.zoom + 1);
            dispatch(wallBuilderInputsActions.setZoom(newZoom));
        },
        zoomOut: () => {
            const newZoom = Math.max(5, inputsState.zoom - 1);
            dispatch(wallBuilderInputsActions.setZoom(newZoom));
        },
        // View mode toggle
        toggleViewMode: () => {
            const newViewMode = inputsState.viewMode === 'front' ? 'top' : 'front';
            dispatch(wallBuilderInputsActions.setViewMode(newViewMode));
        },
        // Reset actions
        resetSegments: () => {
            dispatch(wallBuilderInputsActions.resetSegments());
        },
        resetAll: () => {
            dispatch(wallBuilderInputsActions.resetAll());
        },
    }), [dispatch, inputsState]);
};
// Main hook that combines state and dispatch
export const useWallBuilder = () => {
    const state = useWallBuilderState();
    const dispatch = useWallBuilderDispatch();
    return useMemo(() => ({
        state,
        dispatch,
        actions: dispatch,
    }), [state, dispatch]);
};
// Redux Provider component (for compatibility)
export const WallBuilderProvider = ({ children, initialState }) => {
    // Auto-select first compatible block when wall type changes
    useWallBuilderAutoSelect();
    return _jsx(_Fragment, { children: children });
};
// Auto-select first compatible block when wall type changes
export const useWallBuilderAutoSelect = () => {
    var _a;
    const inputsState = useWallBuilderInputsState();
    const calculations = useWallBuilderCalculations();
    const dispatch = useWallBuilderDispatch();
    useEffect(() => {
        if (inputsState.wallType && calculations.compatibleBlocks.length > 0) {
            // Check if current selected block is still compatible
            const isCurrentBlockCompatible = calculations.compatibleBlocks.find((block) => { var _a; return block.id === ((_a = inputsState.selectedBlock) === null || _a === void 0 ? void 0 : _a.id); });
            if (!isCurrentBlockCompatible) {
                // Select first compatible block
                dispatch.setSelectedBlock(calculations.compatibleBlocks[0]);
            }
        }
    }, [inputsState.wallType, calculations.compatibleBlocks, (_a = inputsState.selectedBlock) === null || _a === void 0 ? void 0 : _a.id, dispatch]);
};
